import React, { createContext, useContext, useEffect } from 'react';
import { useFonts } from 'expo-font';
import {
  Cairo_400Regular,
  Cairo_500Medium,
  Cairo_600SemiBold,
  Cairo_700Bold,
} from '@expo-google-fonts/cairo';
import {
  GoogleSans_400Regular,
  GoogleSans_500Medium,
  GoogleSans_600SemiBold,
  GoogleSans_700Bold,
} from '@expo-google-fonts/google-sans';
import { useLocale } from '../hooks/useLocale';
import { Platform } from 'react-native';
import { typography } from '@tabbio/tokens';

export type FontFamily = {
  regular: string | undefined;
  medium: string | undefined;
  semibold: string | undefined;
  bold: string | undefined;
};

// Latin default → Google Sans (Google's brand typeface, open-sourced under the
// OFL in Nov 2025). Arabic → Cairo.
const GOOGLE_SANS = {
  GoogleSans_400Regular,
  GoogleSans_500Medium,
  GoogleSans_600SemiBold,
  GoogleSans_700Bold,
};

// Weight keys are deliberately stepped down one face: Google Sans reads
// heavier than the old Urbanist set at the same numeric weight, so `medium`
// renders as Regular, `semibold` as Medium, and `bold` as SemiBold to keep the
// UI lighter. (The 700 Bold face is still loaded: the Latin digit badges use
// it directly.)
const latinFonts: FontFamily = {
  regular: 'GoogleSans_400Regular',
  medium: 'GoogleSans_400Regular',
  semibold: 'GoogleSans_500Medium',
  bold: 'GoogleSans_600SemiBold',
};

const arabicFonts: FontFamily = {
  regular: 'Cairo_400Regular',
  medium: 'Cairo_500Medium',
  semibold: 'Cairo_600SemiBold',
  bold: 'Cairo_700Bold',
};


const FontContext = createContext<{
  fonts: FontFamily;
  chatFonts: FontFamily;
  // Cairo (Arabic) faces, exposed regardless of the active locale. Use this for
  // Arabic-script text rendered while the app is in English (e.g. the "العربية"
  // language toggle) so it always renders in Cairo instead of falling back to a
  // glyph-less Latin face.
  arabicFonts: FontFamily;
  // Google Sans (Latin) faces, exposed regardless of the active locale. Use
  // this for content that renders in ITS OWN language rather than the app's —
  // e.g. an English CV document previewed inside the Arabic app.
  latinFonts: FontFamily;
  fontsLoaded: boolean;
}>({
  fonts: latinFonts,
  chatFonts: latinFonts,
  arabicFonts,
  latinFonts,
  fontsLoaded: false,
});

// Inject a Google Font <link> on web (idempotent by id)
function injectWebFont(id: string, href: string) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

// ---------------------------------------------------------------------------
// Arabic glyph fallback on web
// ---------------------------------------------------------------------------
// The web screens (`*.web.tsx`) follow the Gemini design system and set their
// page font explicitly - `'Google Sans Text', 'Google Sans', Roboto, Arial`
// on the page shell, which every descendant then inherits. None of those
// families ships Arabic glyphs, and because the family is set on the element
// itself the Cairo stack applied to <body> (and the Cairo family that AppText
// resolves from `fonts.*`) never gets a chance to apply. The browser silently
// substitutes whatever system face happens to have Arabic coverage - measured
// on the login screen that was Noto Nastaliq Urdu for the h1 and Arial for
// body copy, i.e. a calligraphic display face against a grotesque, neither of
// which matches the geometric sans used for the Latin UI.
//
// Rather than rewrite the font stack in ~54 web screens (and re-break on the
// next one added), register the bundled Cairo faces as *additional faces of
// the Latin families themselves*, restricted with `unicode-range` to the
// Arabic script blocks. Latin text is untouched (no Latin codepoint matches
// the range, so those faces are never candidates); every Arabic codepoint
// resolves to Cairo no matter which of the Latin families an element asks
// for. This is the standard CSS technique for mixed-script stacks and is what
// Google Fonts itself does for its per-subset faces.
const ARABIC_UNICODE_RANGE =
  'U+0600-06FF, U+0750-077F, U+0870-088F, U+08A0-08FF, U+FB50-FDFF, U+FE70-FEFF';

// Latin families used across the web build that need Arabic coverage bolted
// on. They fall into two groups, and the distinction matters - see below.
//
// TODO: the CV document faces - 'Inter' and 'Source Serif 4' (injected as
// `tabbio-cv-document-fonts` below) - are deliberately NOT aliased here.
// Arabic inside a rendered CV therefore still substitutes a system face. That
// is a pre-existing gap, not a regression: CV documents render in their own
// language rather than the app's, and aliasing a sans into the serif template
// would be wrong. Revisit if/when Arabic CV templates ship, which will need
// their own Arabic serif/sans pairing.
//
// Group 1: real CSS families loaded from the Google Fonts CDN by
// WebFontLinks, with genuine faces at 400/500/600/700. Alias one Cairo face
// per weight so bold Arabic stays bold.
const MULTI_WEIGHT_LATIN_FAMILIES = ['Google Sans Text', 'Google Sans'];

// Group 2: the weight-specific family names expo-font registers for the
// bundled Google Sans faces (and that AppText applies while the app is in
// English). expo declares every one of them `font-weight: normal`, yet call
// sites still set fontWeight 500/600/700 alongside them - so an alias
// declared at 500 would out-match the real 400 face on weight and, because
// these families are used with NO fallback in the stack, drop *Latin* text to
// the browser default serif. Measured on /resume-import: the 30px English
// heading rendered in "Times". Mirror expo's own declaration instead - a
// single alias at `normal`, carrying the Cairo weight that matches the face
// it is shadowing - so unicode-range stays the only discriminator.
const SINGLE_WEIGHT_LATIN_FAMILIES: [family: string, cairoFamily: string][] = [
  ['GoogleSans_400Regular', 'Cairo_400Regular'],
  ['GoogleSans_500Medium', 'Cairo_500Medium'],
  ['GoogleSans_600SemiBold', 'Cairo_600SemiBold'],
  ['GoogleSans_700Bold', 'Cairo_700Bold'],
];

const CAIRO_WEIGHTS: [family: string, weight: number][] = [
  ['Cairo_400Regular', 400],
  ['Cairo_500Medium', 500],
  ['Cairo_600SemiBold', 600],
  ['Cairo_700Bold', 700],
];

const ARABIC_FALLBACK_STYLE_ID = 'tabbio-arabic-glyph-fallback';
// How many Cairo weights the currently-injected <style> covers, recorded on
// the element itself so a partial injection can be upgraded later (and so a
// Fast Refresh that keeps the DOM but re-runs this module still sees it).
const COVERED_WEIGHTS_ATTR = 'weights';

// expo-font's web loader writes its @font-face rules into a
// <style id="expo-generated-fonts">. Read the bundled Cairo asset URLs back
// out of the document's stylesheets so the aliases point at the exact same
// (offline-capable) files rather than re-fetching them from a CDN.
//
// This walks every stylesheet rather than looking that one element up by id,
// because expo-font re-creates the element whenever it can't find the previous
// one - a document can legitimately end up with several `expo-generated-fonts`
// styles, each holding a subset of the faces, and an id lookup would only ever
// see the first. Sheets we can't inspect (cross-origin CDN font CSS, a sheet
// still being parsed) throw on `cssRules`; those are skipped, not fatal.
function readBundledCairoSources(): Map<string, string> {
  const sources = new Map<string, string>();
  if (typeof document === 'undefined') return sources;
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRule[];
    try {
      rules = Array.from(sheet.cssRules);
    } catch {
      continue;
    }
    for (const rule of rules) {
      if (typeof CSSFontFaceRule === 'undefined' || !(rule instanceof CSSFontFaceRule)) continue;
      const family = rule.style.fontFamily.replace(/["']/g, '').trim();
      const src = rule.style.getPropertyValue('src');
      // Only the bundled `Cairo_<weight><Name>` faces registered by expo-font.
      // The CDN family is plain 'Cairo' and is deliberately not used here.
      if (family.startsWith('Cairo_') && src) sources.set(family, src);
    }
  }
  return sources;
}

/**
 * Injects (or upgrades) the Arabic glyph fallback stylesheet.
 * Returns true only once ALL four Cairo weights are aliased - expo-font
 * registers its faces one at a time, so a partial snapshot must stay
 * re-injectable rather than being treated as done.
 */
function injectArabicGlyphFallback(): boolean {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return false;

  const existing = document.getElementById(
    ARABIC_FALLBACK_STYLE_ID,
  ) as HTMLStyleElement | null;
  const existingWeights = existing
    ? Number(existing.dataset[COVERED_WEIGHTS_ATTR] ?? '0')
    : 0;
  if (existingWeights >= CAIRO_WEIGHTS.length) return true;

  const sources = readBundledCairoSources();
  const covered = CAIRO_WEIGHTS.filter(([cairoFamily]) => sources.has(cairoFamily));
  // Nothing new to add yet: leave whatever partial coverage is already live.
  if (covered.length === 0 || covered.length <= existingWeights) return false;

  const face = (family: string, cairoFamily: string, weight: string) =>
    `@font-face{font-family:"${family}";src:${sources.get(cairoFamily)};font-weight:${weight};` +
    `font-style:normal;font-display:swap;unicode-range:${ARABIC_UNICODE_RANGE}}`;

  const rules: string[] = [];
  for (const family of MULTI_WEIGHT_LATIN_FAMILIES) {
    for (const [cairoFamily, weight] of covered) rules.push(face(family, cairoFamily, String(weight)));
  }
  for (const [family, cairoFamily] of SINGLE_WEIGHT_LATIN_FAMILIES) {
    if (sources.has(cairoFamily)) rules.push(face(family, cairoFamily, 'normal'));
  }

  const style = existing ?? document.createElement('style');
  style.id = ARABIC_FALLBACK_STYLE_ID;
  style.dataset[COVERED_WEIGHTS_ATTR] = String(covered.length);
  style.textContent = rules.join('\n');
  if (!existing) document.head.appendChild(style);
  return covered.length >= CAIRO_WEIGHTS.length;
}

// Apply the Latin (Google Sans) or Arabic (Cairo) body font stack on web.
function applyWebFontStack(isArabic: boolean) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  document.body.style.fontFamily = isArabic
    ? typography.fontFamily.webArabic
    : typography.fontFamily.webPoppins;
}

export function FontProvider({ children }: { children: React.ReactNode }) {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';

  // Google Sans (Latin) is the primary UI font. Cairo is always loaded too
  // for every locale, so Arabic-script text shown inside an English
  // UI (the "العربية" toggle, user-entered Arabic names, etc.) still renders in
  // Cairo rather than a glyph-less Latin fallback. On web the faces register as
  // @font-face by expo-font via the same require()s, so `fonts.*` names resolve
  // there too (with the body stack fallback below while the bundled face loads).
  const [fontsLoaded] = useFonts({
    ...GOOGLE_SANS,
    Cairo_400Regular,
    Cairo_500Medium,
    Cairo_600SemiBold,
    Cairo_700Bold,
  });

  // Always inject the Cairo web font (not just for Arabic locale) so Arabic-script
  // text rendered while the UI is in English still resolves to Cairo on web.
  useEffect(() => {
    injectWebFont(
      'tabbio-cairo-font',
      'https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap',
    );
    // CV document faces. The shared WebCvRenderer asks for the literal families
    // 'Inter' / 'Source Serif 4' (apps/web serves them via next/font variables
    // instead), and the PDF/DOCX exports embed the same faces — loading them
    // here keeps the in-app web preview typographically identical to both.
    injectWebFont(
      'tabbio-cv-document-fonts',
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Source+Serif+4:ital,wght@0,400;0,600;0,700;1,400&display=swap',
    );
  }, []);

  useEffect(() => {
    applyWebFontStack(isArabic);
  }, [isArabic]);

  // Bolt Arabic (Cairo) coverage onto the Latin families the web screens set
  // explicitly, so Arabic text never falls through to a system serif/
  // calligraphic face.
  //
  // expo-font writes its @font-face block asynchronously and its stylesheet
  // can be unreadable for a while (cold bundle, throttled network, a sheet
  // mid-parse), so this retries until every Cairo weight is aliased. There is
  // deliberately NO give-up cap: silently losing the fallback for the rest of
  // the session is exactly the failure it exists to prevent, and the slow
  // devices where that happens are the ones that need it. Two triggers, so a
  // late registration is caught whether or not it mutates the DOM we watch:
  //  - a MutationObserver on <head>, for instant pickup of expo-font's append;
  //  - a backing-off poll (100ms -> 2s), for the cases the observer can't see.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    if (injectArabicGlyphFallback()) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let observer: MutationObserver | undefined;
    let delay = 100;
    let done = false;

    const teardown = () => {
      done = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      observer?.disconnect();
      observer = undefined;
    };

    const attempt = () => {
      if (done) return true;
      if (injectArabicGlyphFallback()) {
        teardown();
        return true;
      }
      return false;
    };

    const poll = () => {
      if (attempt()) return;
      delay = Math.min(2000, Math.round(delay * 1.5));
      timer = setTimeout(poll, delay);
    };

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => attempt());
      observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    }
    timer = setTimeout(poll, delay);

    return teardown;
    // Re-armed on locale change too: switching to Arabic is the moment the
    // fallback actually matters, so give it a fresh attempt then.
  }, [fontsLoaded, isArabic]);

  const fonts = isArabic ? arabicFonts : latinFonts;
  const chatFonts = fonts;

  return (
    <FontContext.Provider
      value={{ fonts, chatFonts, arabicFonts, latinFonts, fontsLoaded }}
    >
      {children}
    </FontContext.Provider>
  );
}

/**
 * Returns the locale-appropriate font family names.
 * Latin → Google Sans, Arabic → Cairo. On web these native names fall back to
 * the body font stack applied by FontProvider, so usage is uniform:
 * ```tsx
 * const { fonts } = useFont();
 * <Text style={{ fontFamily: fonts.regular }}>Hello</Text>
 * <Text style={{ fontFamily: fonts.medium }}>Bold text</Text>
 * ```
 * Most call sites should import `Text`/`TextInput` from
 * `@/components/common/AppText`, which applies these automatically by weight.
 */
export function useFont() {
  return useContext(FontContext);
}
